import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  ShoppingCart,
  Search,
  Plus,
  Minus,
  Trash2,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  Printer,
  Receipt,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  History,
  Tag,
  ArrowRight,
  Filter,
  DollarSign,
  ShoppingBag
} from 'lucide-react';
import { useBranch } from '../../contexts/BranchContext';
import { useAuth } from '../../contexts/AuthContext';
import BillPrintModal from '../../components/Billing/BillPrintModal';
import { format } from 'date-fns';

const Billing = () => {
  const { selectedBranch } = useBranch();
  const { currencySymbol } = useAuth();
  const queryClient = useQueryClient();

  // Active View Tab: 'billing' | 'history'
  const [activeTab, setActiveTab] = useState('billing');

  // Product catalog state
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');

  // Customer state
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerGst, setCustomerGst] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Cart & Payment state
  const [cart, setCart] = useState([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [paidAmount, setPaidAmount] = useState(0);

  // Bill print modal state
  const [printedBillData, setPrintedBillData] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Sales history state
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);

  // 1. Fetch Products
  const { data: productsData, isLoading: productsLoading, refetch: refetchProducts } = useQuery(
    ['products', selectedBranch?.id],
    async () => {
      const response = await api.get('/api/products', {
        params: { branchId: selectedBranch?.id }
      });
      return response.data.data;
    },
    { enabled: !!selectedBranch }
  );

  // 2. Fetch Categories
  const { data: categoriesData } = useQuery(
    'categories',
    async () => {
      const response = await api.get('/api/categories');
      return response.data.data;
    }
  );

  // 3. Search Customer on Phone change
  useEffect(() => {
    if (customerPhone && customerPhone.length >= 3) {
      const searchTimer = setTimeout(async () => {
        try {
          const res = await api.get('/api/customers', {
            params: { search: customerPhone, limit: 5 }
          });
          const customers = res.data?.data?.customers || [];
          setCustomerSearchResults(customers);
          setShowCustomerDropdown(customers.length > 0);
        } catch (err) {
          console.error("Customer search error:", err);
        }
      }, 300);
      return () => clearTimeout(searchTimer);
    } else {
      setCustomerSearchResults([]);
      setShowCustomerDropdown(false);
    }
  }, [customerPhone]);

  const selectExistingCustomer = (cust) => {
    setCustomerName(cust.name || '');
    setCustomerPhone(cust.phone || '');
    setCustomerEmail(cust.email || '');
    setCustomerAddress(cust.address || '');
    setCustomerGst(cust.gstNumber || '');
    setShowCustomerDropdown(false);
    toast.success(`Selected customer: ${cust.name}`);
  };

  // 4. Cart Operations
  const addToCart = (product) => {
    if ((product.currentStock || 0) <= 0) {
      toast.error(`"${product.name}" is out of stock!`);
      return;
    }

    setCart(prev => {
      const existing = prev.find(item => item.productId === product.id);
      if (existing) {
        if (existing.quantity >= product.currentStock) {
          toast.error(`Cannot add more than available stock (${product.currentStock})`);
          return prev;
        }
        return prev.map(item =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [
          ...prev,
          {
            productId: product.id,
            productName: product.name,
            partNumber: product.partNumber || '',
            hsnCode: product.hsnCode || '',
            rate: product.saleRate || 0,
            gstRate: product.gstRate || 0,
            quantity: 1,
            unit: product.unit || 'PCS',
            maxStock: product.currentStock || 0
          }
        ];
      }
    });
    toast.success(`Added ${product.name} to bill`);
  };

  const updateCartQty = (productId, delta) => {
    setCart(prev =>
      prev.map(item => {
        if (item.productId === productId) {
          const newQty = item.quantity + delta;
          if (newQty > item.maxStock) {
            toast.error(`Stock limit reached (${item.maxStock} PCS)`);
            return item;
          }
          if (newQty <= 0) return null;
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean)
    );
  };

  const updateCartRate = (productId, rate) => {
    const parsed = parseFloat(rate) || 0;
    setCart(prev =>
      prev.map(item =>
        item.productId === productId ? { ...item, rate: parsed } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.productId !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setDiscountAmount(0);
    setNotes('');
    setReferenceNumber('');
  };

  // Cart Calculations
  const subtotal = cart.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  const totalGst = cart.reduce((sum, item) => {
    const itemSub = item.quantity * item.rate;
    return sum + (itemSub * (item.gstRate || 0) / 100);
  }, 0);
  const grandTotal = Math.max(0, (subtotal + totalGst) - parseFloat(discountAmount || 0));

  // 5. Create Sale Bill Mutation
  const createBillMutation = useMutation(
    async (billPayload) => {
      const response = await api.post('/api/billing/create', billPayload);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Bill created successfully!');
        setPrintedBillData(data.data);
        setShowPrintModal(true);
        clearCart();
        // Reset customer
        setCustomerName('');
        setCustomerPhone('');
        setCustomerEmail('');
        setCustomerAddress('');
        setCustomerGst('');
        queryClient.invalidateQueries('products');
        queryClient.invalidateQueries('sale-bills');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create bill');
      }
    }
  );

  const handleGenerateBill = (e) => {
    e.preventDefault();

    if (!customerName || !customerPhone) {
      toast.error('Please enter Customer Name and Phone Number');
      return;
    }

    if (cart.length === 0) {
      toast.error('Cart is empty. Please select products to sell.');
      return;
    }

    createBillMutation.mutate({
      customerName,
      customerPhone,
      customerEmail,
      customerAddress,
      customerGst,
      items: cart,
      discountAmount: parseFloat(discountAmount || 0),
      paymentMode,
      referenceNumber,
      notes,
      paidAmount: paymentMode === 'CREDIT' ? parseFloat(paidAmount || 0) : grandTotal,
      branchId: selectedBranch?.id
    });
  };

  // 6. Sales History Query
  const { data: salesHistoryData, isLoading: historyLoading } = useQuery(
    ['sale-bills', selectedBranch?.id, historyPage, historySearch],
    async () => {
      const response = await api.get('/api/billing/sales', {
        params: {
          branchId: selectedBranch?.id,
          page: historyPage,
          limit: 10,
          search: historySearch
        }
      });
      return response.data.data;
    },
    { enabled: activeTab === 'history' && !!selectedBranch }
  );

  // Fetch bill for reprint
  const handleReprintBill = async (billId) => {
    try {
      toast.loading('Loading bill details...');
      const res = await api.get(`/api/billing/sales/${billId}`);
      toast.dismiss();
      if (res.data?.success) {
        setPrintedBillData(res.data.data);
        setShowPrintModal(true);
      }
    } catch (err) {
      toast.dismiss();
      toast.error('Failed to load bill for re-print');
    }
  };

  // Filter products by search & category
  const productsList = Array.isArray(productsData?.products)
    ? productsData.products
    : Array.isArray(productsData)
    ? productsData
    : [];

  const filteredProducts = productsList.filter(p => {
    const matchesSearch =
      !productSearch ||
      p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.brand?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.model?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.partNumber?.toLowerCase().includes(productSearch.toLowerCase());

    const matchesCategory =
      selectedCategory === 'ALL' ||
      p.categoryId === selectedCategory ||
      p.category?.name === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-600">
            <Receipt className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Direct Item Sale & Billing</h1>
            <p className="text-xs text-slate-500">Sell products directly to walk-in customers with auto stock deduction & print bills</p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="bg-slate-100 p-1.5 rounded-xl flex items-center space-x-1 border border-slate-200">
          <button
            onClick={() => setActiveTab('billing')}
            className={`px-4 py-2 rounded-lg font-bold text-xs transition-all flex items-center space-x-2 ${
              activeTab === 'billing'
                ? 'bg-emerald-600 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <ShoppingCart className="w-4 h-4" />
            <span>POS Billing</span>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-lg font-bold text-xs transition-all flex items-center space-x-2 ${
              activeTab === 'history'
                ? 'bg-emerald-600 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <History className="w-4 h-4" />
            <span>Sales History & Bills</span>
          </button>
        </div>
      </div>

      {activeTab === 'billing' ? (
        /* POS Billing View */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Column: Product Catalog (7 Columns) */}
          <div className="lg:col-span-7 space-y-4">
            
            {/* Search & Filter Bar */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
              <div className="relative">
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Search products by Name, Brand, Model, Part Number..."
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <Search className="w-5 h-5 text-slate-400 absolute left-3 top-3" />
              </div>

              {/* Category Pills */}
              <div className="flex items-center space-x-2 overflow-x-auto pb-1 text-xs">
                <button
                  onClick={() => setSelectedCategory('ALL')}
                  className={`px-3 py-1.5 rounded-lg font-bold shrink-0 transition-colors ${
                    selectedCategory === 'ALL'
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  All Items
                </button>
                {(Array.isArray(categoriesData) ? categoriesData : categoriesData?.categories || []).map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3 py-1.5 rounded-lg font-bold shrink-0 transition-colors ${
                      selectedCategory === cat.id
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Products Grid */}
            {productsLoading ? (
              <div className="flex items-center justify-center h-48 bg-white rounded-xl border">
                <div className="spinner w-8 h-8 text-emerald-500"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-slate-400 space-y-2">
                <AlertCircle className="w-10 h-10 mx-auto text-slate-300" />
                <p className="font-bold text-slate-700">No products found</p>
                <p className="text-xs">Try adjusting your search query or category filter</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[620px] overflow-y-auto pr-1">
                {filteredProducts.map((product) => {
                  const inCart = cart.find(i => i.productId === product.id);
                  const isOutOfStock = (product.currentStock || 0) <= 0;

                  return (
                    <div
                      key={product.id}
                      className={`bg-white border rounded-xl p-4 flex flex-col justify-between transition-all hover:shadow-md ${
                        isOutOfStock ? 'opacity-60 border-red-200 bg-red-50/20' : 'border-slate-200 hover:border-emerald-300'
                      }`}
                    >
                      <div>
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider bg-emerald-50 px-2 py-0.5 rounded">
                              {product.category?.name || 'Item'}
                            </span>
                            <h3 className="font-bold text-slate-900 text-sm mt-1 leading-snug">{product.name}</h3>
                          </div>
                          
                          {/* Stock badge */}
                          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                            isOutOfStock
                              ? 'bg-red-100 text-red-700'
                              : product.currentStock <= product.minStockLimit
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            Stock: {product.currentStock} {product.unit || 'PCS'}
                          </span>
                        </div>

                        <p className="text-xs text-slate-500 mt-1 font-mono">
                          {product.brand} {product.model} {product.partNumber && `| P/N: ${product.partNumber}`}
                        </p>
                      </div>

                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                        <div>
                          <span className="text-[10px] text-slate-400 block font-semibold">SALE RATE</span>
                          <span className="text-base font-black text-slate-900">
                            {currencySymbol}{product.saleRate?.toFixed(2)}
                          </span>
                        </div>

                        <button
                          onClick={() => addToCart(product)}
                          disabled={isOutOfStock}
                          className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-1 ${
                            inCart
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                              : isOutOfStock
                              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                              : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm'
                          }`}
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>{inCart ? `Added (${inCart.quantity})` : 'Add to Bill'}</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>

          {/* Right Column: Customer Details & Bill Cart Drawer (5 Columns) */}
          <div className="lg:col-span-5 space-y-4">
            
            <form onSubmit={handleGenerateBill} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              
              {/* Customer Information Header */}
              <div className="bg-slate-900 text-white px-5 py-3.5 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-emerald-400" />
                  <h2 className="font-bold text-sm">Customer Information</h2>
                </div>
                <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Required for Bill</span>
              </div>

              <div className="p-4 space-y-3 relative">
                
                {/* Phone & Auto-complete Search */}
                <div className="relative">
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Phone Number * <span className="text-slate-400 font-normal">(Type to search customer)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="e.g. 9876543210"
                      className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-xs font-bold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                      required
                    />
                    <Phone className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                  </div>

                  {/* Customer Auto-complete Dropdown */}
                  {showCustomerDropdown && (
                    <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                      <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-bold text-slate-500 border-b">
                        Existing Customers Found:
                      </div>
                      {customerSearchResults.map(cust => (
                        <div
                          key={cust.id}
                          onClick={() => selectExistingCustomer(cust)}
                          className="p-2.5 hover:bg-emerald-50 cursor-pointer border-b border-slate-100 text-xs transition-colors"
                        >
                          <p className="font-bold text-slate-900">{cust.name} ({cust.phone})</p>
                          {cust.address && <p className="text-[10px] text-slate-500 truncate">{cust.address}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Customer Name *</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Customer full name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-bold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Email & Address & GST */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-0.5">Email</label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="Optional"
                      className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-0.5">GSTIN</label>
                    <input
                      type="text"
                      value={customerGst}
                      onChange={(e) => setCustomerGst(e.target.value)}
                      placeholder="Optional GST"
                      className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs uppercase focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-0.5">Address</label>
                  <input
                    type="text"
                    value={customerAddress}
                    onChange={(e) => setCustomerAddress(e.target.value)}
                    placeholder="Customer city/address (optional)"
                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                </div>

              </div>

              {/* Cart Items Table Header */}
              <div className="bg-slate-100 border-t border-b border-slate-200 px-4 py-2 flex justify-between items-center text-xs font-bold text-slate-700">
                <span className="flex items-center space-x-1.5">
                  <ShoppingCart className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Selected Products ({cart.length})</span>
                </span>
                {cart.length > 0 && (
                  <button
                    type="button"
                    onClick={clearCart}
                    className="text-red-600 hover:text-red-700 text-[11px] font-bold underline"
                  >
                    Clear All
                  </button>
                )}
              </div>

              {/* Cart List */}
              <div className="p-3 max-h-56 overflow-y-auto divide-y divide-slate-100">
                {cart.length === 0 ? (
                  <div className="py-6 text-center text-slate-400 space-y-1">
                    <ShoppingBag className="w-8 h-8 mx-auto text-slate-300" />
                    <p className="text-xs font-bold text-slate-600">Bill Cart is empty</p>
                    <p className="text-[11px]">Click "+ Add to Bill" on products to start</p>
                  </div>
                ) : (
                  cart.map((item) => (
                    <div key={item.productId} className="py-2 flex items-center justify-between text-xs gap-2">
                      <div className="flex-1">
                        <p className="font-bold text-slate-900 leading-tight">{item.productName}</p>
                        <p className="text-[10px] text-slate-500">
                          Rate: {currencySymbol}
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => updateCartRate(item.productId, e.target.value)}
                            className="w-14 px-1 py-0.5 border border-slate-200 rounded ml-1 font-bold text-slate-800 text-[10px]"
                          />
                        </p>
                      </div>

                      {/* Quantity Selector */}
                      <div className="flex items-center space-x-1 bg-slate-100 p-0.5 rounded-lg border">
                        <button
                          type="button"
                          onClick={() => updateCartQty(item.productId, -1)}
                          className="w-6 h-6 bg-white hover:bg-slate-200 rounded flex items-center justify-center font-bold text-slate-700"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-6 text-center font-black text-slate-900 text-xs">{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateCartQty(item.productId, 1)}
                          className="w-6 h-6 bg-white hover:bg-slate-200 rounded flex items-center justify-center font-bold text-slate-700"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      <div className="text-right w-20">
                        <span className="font-black text-slate-900 block">
                          {currencySymbol}{(item.quantity * item.rate).toFixed(2)}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeFromCart(item.productId)}
                        className="text-slate-400 hover:text-red-600 p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Financial Calculation Drawer */}
              <div className="bg-slate-50 border-t border-slate-200 p-4 space-y-3">
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal:</span>
                    <span className="font-bold">{currencySymbol}{subtotal.toFixed(2)}</span>
                  </div>

                  {totalGst > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>GST Tax:</span>
                      <span className="font-bold">+{currencySymbol}{totalGst.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center py-1">
                    <span className="text-slate-700 font-bold">Extra Discount ({currencySymbol}):</span>
                    <input
                      type="number"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      className="w-20 px-2 py-1 bg-white border border-slate-300 rounded text-right font-bold text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t border-slate-300 text-sm font-black text-slate-900">
                    <span>TOTAL PAYABLE:</span>
                    <span className="text-emerald-700 text-base">{currencySymbol}{grandTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Payment Mode Selector */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Payment Mode</label>
                    <select
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                      className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    >
                      <option value="CASH">Cash</option>
                      <option value="UPI">UPI / GPay / PhonePe</option>
                      <option value="CARD">Debit/Credit Card</option>
                      <option value="BANK">Bank Transfer</option>
                      <option value="CREDIT">Credit (Due)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Ref / Txn ID</label>
                    <input
                      type="text"
                      value={referenceNumber}
                      onChange={(e) => setReferenceNumber(e.target.value)}
                      placeholder="Txn ID (optional)"
                      className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>

                {paymentMode === 'CREDIT' && (
                  <div>
                    <label className="block text-[10px] font-bold text-red-600 uppercase mb-0.5">Paid Advance / Partial ({currencySymbol})</label>
                    <input
                      type="number"
                      value={paidAmount}
                      onChange={(e) => setPaidAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-2 py-1 bg-white border border-red-200 rounded-lg text-xs font-bold text-red-700"
                    />
                  </div>
                )}

                {/* Submit Action */}
                <button
                  type="submit"
                  disabled={createBillMutation.isLoading || cart.length === 0}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 active:from-emerald-700 active:to-teal-700 text-white font-extrabold py-3 rounded-xl text-sm transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg shadow-emerald-950/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createBillMutation.isLoading ? (
                    <>
                      <div className="spinner w-4 h-4 border-white" />
                      <span>Generating Bill & Deducting Stock...</span>
                    </>
                  ) : (
                    <>
                      <Printer className="w-4 h-4" />
                      <span>Generate Bill & Print Thermal/A4</span>
                    </>
                  )}
                </button>

              </div>
            </form>

          </div>

        </div>
      ) : (
        /* Sales History View */
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b pb-4">
            <h2 className="text-lg font-bold text-slate-900">Direct Sales History & Bills</h2>
            <div className="relative w-full sm:w-72">
              <input
                type="text"
                value={historySearch}
                onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
                placeholder="Search Bill #, Customer name or phone..."
                className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
            </div>
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="spinner w-8 h-8 text-emerald-500"></div>
            </div>
          ) : (salesHistoryData?.bills || []).length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-2" />
              <p className="font-bold text-slate-700">No sale bills found</p>
              <p className="text-xs">Generations of bills in POS mode will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50 text-slate-700 text-[11px] font-bold uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">Bill Number</th>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Customer Name</th>
                    <th className="px-4 py-3 text-left">Phone</th>
                    <th className="px-4 py-3 text-center">Items</th>
                    <th className="px-4 py-3 text-left">Payment</th>
                    <th className="px-4 py-3 text-right">Total Amount</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100 text-xs">
                  {salesHistoryData.bills.map((bill) => (
                    <tr key={bill.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-bold text-emerald-700 font-mono">{bill.billNumber}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {bill.billDate ? format(new Date(bill.billDate), 'dd/MM/yyyy hh:mm a') : ''}
                      </td>
                      <td className="px-4 py-3 font-bold text-slate-900">{bill.customerName}</td>
                      <td className="px-4 py-3 text-slate-600">{bill.customerPhone}</td>
                      <td className="px-4 py-3 text-center font-bold text-slate-800">{bill.itemsCount}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold uppercase text-slate-700">
                          {bill.paymentMode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-black text-slate-900">
                        {currencySymbol}{bill.totalAmount.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleReprintBill(bill.id)}
                          className="px-3 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-[11px] inline-flex items-center space-x-1 transition-colors"
                        >
                          <Printer className="w-3 h-3" />
                          <span>View & Print</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Bill Print Modal Component */}
      <BillPrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        billData={printedBillData}
      />
    </div>
  );
};

export default Billing;
